import React, { useState, useEffect } from "react";
import { isAdmin, handleError, getUserNameArray } from "src/Common/function";
import AddModal from "./AddModal";
import UserService from "src/Services/API/user.service";
import ReportService from "src/Services/API/report.service";
import RoleService from "src/Services/API/role.service";
import ReportTable from "./ReportTable";
import { dayOfWeekList, autoReportTypeArray } from "src/Common/contants";
import { getOrdinalSuffix } from "src/Common/common";
import { useSearchParams } from "react-router-dom";
import ConfirmDialog from "src/Common/confirmdialog";

const AutoReport = ({ createSignal, onCreateSignalConsumed }) => {
  const [searchParams] = useSearchParams();
  const branchId = Number(searchParams.get("branch"));

  const currentUser = UserService.getCurrentUser();

  const [roleArray, setRoleArray] = useState([]);
  const [technicianArray, setTechnicianArray] = useState([]);
  const [userArray, setUserArray] = useState([]);

  const [showModal, setShowModal] = useState(false);
  const [reportArray, setReportArray] = useState([]);
  const [reportResponse, setReportResponse] = useState(null);

  //edit
  const [editingReport, setEditingReport] = useState(null);

  const [isLoading, setIsLoading] = useState(false);

  //delete confirm
  const [isDeleteConfirmShown, setIsDeleteConfirmShown] = useState(false);
  const [indexForDeletion, setIndexForDeletion] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      setIsLoading(true);
      try {
        const roleResponse = await RoleService.getAll();
        let roles = [];
        if (roleResponse.status === 200) {
          // Every company has these roles (seeded per-company), so build the
          // ordered list for all tenants, not just rscs.
          const roleOrder = ["Project Manager", "Manager", "Technician"];
          roles = roleResponse.data
            .filter((role) => roleOrder.includes(role.name))
            .sort((a, b) => roleOrder.indexOf(a.name) - roleOrder.indexOf(b.name));
        }

        for (let role of roles) {
          const usersResponse = await UserService.getUsersByRoleWithBranchId(role.name, branchId);
          if (usersResponse.status === 200) {
            role.users = usersResponse.data.filter((user) => user.isEnabled === true);
          }
        }
        setRoleArray(roles);

        // Fetch all users and technicians concurrently
        const [allUsersResponse, techniciansResponse, managersResponse, projectManagersResponse] = await Promise.all([
          UserService.getAll(branchId),
          UserService.getUsersByRoleWithBranchId("Technician", branchId),
          UserService.getUsersByRoleWithBranchId("Manager", branchId),
          UserService.getUsersByRoleWithBranchId("Project Manager", branchId),
        ]);
        // Check if both requests were successful
        if (allUsersResponse.status === 200) {
          setUserArray(allUsersResponse.data.filter((user) => user.isEnabled === true));
        }
        let tempTechnicians = [];
        if (techniciansResponse.status === 200) {
          tempTechnicians = techniciansResponse.data.filter((user) => user.isEnabled === true);
        }
        if (managersResponse.status === 200) {
          tempTechnicians = [...tempTechnicians, ...managersResponse.data.filter((user) => user.isEnabled === true)];
        }
        if (projectManagersResponse.status === 200) {
          tempTechnicians = [
            ...tempTechnicians,
            ...projectManagersResponse.data.filter((user) => user.isEnabled === true),
          ];
        }
        setTechnicianArray(tempTechnicians);
      } catch (error) {
        handleError(error);
        setIsLoading(false);
      }
    };
    fetchData();
  }, [branchId]);

  useEffect(() => {
    fetchReports();
  }, [userArray, technicianArray]);

  // The header "Create Scheduled Report" button bumps createSignal to open the modal.
  // Notify the parent so it can reset the signal back to 0; otherwise a stale
  // non-zero signal would re-open the modal when this component re-mounts
  // (e.g. after switching to the Ad-Hoc tab and back).
  useEffect(() => {
    if (createSignal) {
      setEditingReport(null);
      setShowModal(true);
      onCreateSignalConsumed?.();
    }
  }, [createSignal]);

  const fetchReports = async () => {
    const reportsResponse = await ReportService.getAll(branchId);
    if (reportsResponse.status === 200) {
      const reports = reportsResponse.data.map((report) => {
        const time = report.time.slice(0, -3);
        const cycleLabel = report.cycle === 0 ? "Weekly" : report.cycle === 1 ? "Monthly" : "Daily";
        const scheduleDetail =
          report.cycle === 0
            ? `${dayOfWeekList[report.day]} ${time}`
            : report.cycle === 1
            ? `Every ${getOrdinalSuffix(report.day)} at ${time}`
            : `Every ${time}`;
        // When no technicians are stored, the report applies to all technicians.
        const technicianIds = (report.technicians ?? "").split(",").filter((id) => id.trim() !== "");
        return {
          type:
            report.type === 20 && report.cycle === 2
              ? "Daily Report"
              : autoReportTypeArray.find((type) => type.value === report.type)?.name ?? "Unknown",
          id: report.id,
          technicians:
            technicianIds.length > 0 ? getUserNameArray(technicianIds, technicianArray) : "All Technicians",
          recipients: getUserNameArray(report.recipients.split(","), userArray),
          cycleLabel,
          scheduleDetail,
          status: "Active",
        };
      });

      setReportArray(reports);
      setReportResponse(reportsResponse.data);
    }
    setIsLoading(false);
  };

  const save = (data) => {
    if (isAdmin()) {
      data["branch"] = branchId;
    } else {
      data["branch"] = currentUser.branches[0].id;
    }
    ReportService.create(data)
      .then((response) => {
        setShowModal(false);
        fetchReports();
      })
      .catch((error) => {
        console.log("🚀 ~ ReportService.create ~ error:", error);
      });
  };

  const requestDelete = (index) => {
    setIndexForDeletion(index);
    setIsDeleteConfirmShown(true);
  };

  const deleteReport = (index) => {
    console.log(reportArray[index]);
    ReportService.deleteReport(reportArray[index].id)
      .then((response) => {
        console.log("🚀 ~ save ~ response:", response);
        fetchReports();
      })
      .catch((error) => {
        console.log("🚀 ~ ReportService.create ~ error:", error);
      });
  };

  const editReport = (index) => {
    setEditingReport(reportResponse[index]);
    setShowModal(true);
  };

  const update = (data) => {
    ReportService.update(editingReport.id, data)
      .then((response) => {
        console.log("🚀 ~ update ~ response:", response);
        setShowModal(false);
        fetchReports();
      })
      .catch((error) => {
        console.log("🚀 ~ ReportService.update ~ error:", error);
      });
  };

  return (
    <div>
      <AddModal
        shown={showModal}
        onCancelClicked={() => setShowModal(false)}
        userArray={userArray}
        roleArray={roleArray}
        technicianArray={technicianArray}
        save={save}
        update={update}
        editingReport={editingReport}
      />
      <ConfirmDialog
        title="DELETE REPORT"
        description="Are you sure you want to delete this report?"
        shown={isDeleteConfirmShown}
        onConfirmClicked={() => {
          if (indexForDeletion !== null) {
            deleteReport(indexForDeletion);
          }
          setIsDeleteConfirmShown(false);
          setIndexForDeletion(null);
        }}
        onCancelClicked={() => {
          setIsDeleteConfirmShown(false);
          setIndexForDeletion(null);
        }}
      />
      <ReportTable
        reports={reportArray}
        onEditClicked={editReport}
        onDeleteClicked={requestDelete}
        isLoading={isLoading}
      />
    </div>
  );
};

export default AutoReport;
